'use client';
import { Selection } from '@heroui/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  Button,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Select,
  SelectItem,
  useDisclosure,
} from '@/components/ui';
import { uiConfig } from '@/configs/ui.config';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import { useTranslation } from '@/providers/app-provider/app-provider';
import {
  useStudioPreviewPortalRoot,
  useStudioPreviewScrollLock,
} from '@/providers/studio-preview-portal-provider';
import { parseSortBySearchParam, SortByItemKeys } from '@/types/common/sort-by-item-keys';
import { cn } from '@/utils';
import { studioPreviewPortalBottomSheetClassNames } from '@/utils/studio-preview-portal-modal';

const ProductsSortBy = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useProductListingSearchParams();
  const portalRoot = useStudioPreviewPortalRoot();
  const constrainModalToStudioPortal = portalRoot != null;
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  useStudioPreviewScrollLock(isOpen && constrainModalToStudioPortal);
  const items = uiConfig.getSortByItems(t);
  const itemsEntries = Object.entries(items);

  const sortParam = parseSortBySearchParam(searchParams.get('sort-by'));
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([sortParam]));

  useEffect(() => {
    setSelectedKeys(new Set([sortParam]));
  }, [sortParam]);

  const selectedKey = parseSortBySearchParam(String(Array.from(selectedKeys)[0] ?? ''));
  const mobileSortLabel =
    items[selectedKey] ?? items[SortByItemKeys.NONE] ?? t?.products.sortBy ?? '';

  const handleSelection = (keys: Selection) => {
    setSelectedKeys(keys);
    const newSearchParams = new URLSearchParams(window.location.search);
    const newSortKey = Array.from(keys)[0] as SortByItemKeys;

    if (newSortKey === SortByItemKeys.NONE) {
      newSearchParams.delete('sort-by');
    } else {
      newSearchParams.set('sort-by', newSortKey);
    }
    newSearchParams.set('page', '1');
    const query = newSearchParams.toString();
    const pathname = window.location.pathname;
    router.push(query ? `${pathname}?${query}` : pathname);
  };

  return (
    <>
      <Select
        data-testid="products-sort-by__select"
        placeholder={t?.products.sortBy}
        className="hidden max-w-56 @sm:block"
        classNames={{
          trigger: ['bg-white', 'shadow-none'],
        }}
        variant="bordered"
        aria-label="Sort by select"
        selectionMode="single"
        selectedKeys={sortParam === SortByItemKeys.NONE ? new Set() : selectedKeys}
        onSelectionChange={handleSelection}
        items={itemsEntries}
      >
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (([sortByKey, sortByLabel]: any) => (
            <SelectItem
              data-testid={`products-sort-by__${sortByKey}`}
              key={sortByKey}
              isDisabled={sortByKey === SortByItemKeys.NONE}
            >
              {sortByLabel}
            </SelectItem>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          )) as any
        }
      </Select>

      <Button
        data-testid="products-sort-by__button"
        onClick={onOpen}
        className="flex justify-between @sm:hidden"
        variant="flat"
      >
        {mobileSortLabel}
      </Button>

      <Modal
        data-testid="products-sort-by__modal"
        size="2xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        hideCloseButton
        placement="bottom"
        portalContainer={portalRoot ?? undefined}
        classNames={
          constrainModalToStudioPortal ? studioPreviewPortalBottomSheetClassNames : undefined
        }
      >
        <ModalContent className={cn('bg-background', constrainModalToStudioPortal && 'max-h-full')}>
          <ModalBody className="flex flex-col items-center">
            <Listbox
              data-testid="products-sort-by__listbox"
              classNames={{ list: 'flex items-center' }}
              aria-label="Sort by list"
              items={itemsEntries}
              selectionMode="single"
              selectedKeys={selectedKeys}
              onSelectionChange={keys => {
                handleSelection(keys);
                onOpenChange();
              }}
            >
              {([sortByKey, sortByLabel]) => (
                <ListboxItem
                  data-testid={`products-sort-by__${sortByKey}`}
                  key={sortByKey}
                  aria-label={sortByLabel}
                  isDisabled={sortByKey === SortByItemKeys.NONE}
                  className={cn('h-12 px-6', sortByKey === selectedKey && 'bg-gray-300')}
                >
                  {sortByLabel}
                </ListboxItem>
              )}
            </Listbox>
          </ModalBody>
          <ModalFooter className="flex justify-center">
            <Button
              data-testid="products-sort-by__modal-close-button"
              className="w-4/5"
              color="primary"
              variant="flat"
              onClick={onOpenChange}
            >
              {t?.products.back}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProductsSortBy;
